/**
 * Alerts (/dashboard/admin/alerts): ward email alerts for Chicago Politics staff (read-only; worker/src/ward-alerts.js,
 * GET /api/dashboard/admin/alerts). Not designed: built from the Analysis page's parts (cards, rules, members table).
 * Subscribers per ward by status and alert, the send log by meeting and ward (last 60 days), and the latest send errors.
 * Never shows an address.
 */
import { useEffect, useState } from "react";
import { List, ListRow, RowSub, RowTitle } from "../components/shared/List";
import { Cell, Head } from "./Organizations";

export interface AlertsReport {
  sending: boolean;
  totals: Record<"active" | "pending" | "unsubscribed" | "bounced" | "complained", number>;
  wards: { ward: number; active: number; votes: number; agenda: number; pending: number; unsubscribed: number; bounced: number; complained: number }[];
  sends: { kind: "votes" | "agenda"; meetingId: string; body: string | null; date: string | null; ward: number; sent: number; failed: number; bounced: number; sending: number; items: number | null; lastAt: string }[];
  errors: { kind: string; meetingId: string; ward: number; error: string | null; at: string }[];
}

const count = (n: number) => n.toLocaleString("en-US");
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const KIND = { votes: "How they voted", agenda: "Up for a vote" } as const;

export function AdminAlerts() {
  const [r, setR] = useState<AlertsReport | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { document.title = "Alerts · Chicago Politics"; }, []);
  useEffect(() => {
    fetch("/api/dashboard/admin/alerts", { credentials: "include" }).then((x) => (x.ok ? x.json() : Promise.reject())).then(setR).catch(() => setFailed(true));
  }, []);
  const wards = r ? r.wards.filter((w) => w.active || w.pending || w.unsubscribed || w.bounced || w.complained) : [];
  return (
    <div className="access-page admin-page">
      <header className="access-head">
        <h1 className="page-title">Alerts</h1>
        {r && <span className={`chip ${r.sending ? "chip--positive" : "chip--neutral"}`}>{r.sending ? "Sending" : "Off"}</span>}
      </header>
      {failed ? <p className="empty-panel">Couldn't load alerts. Try again.</p> : !r ? <p className="empty-panel">Loading…</p> : <>
        <div className="access-cards admin-analysis">
          <section className="access-card" aria-labelledby="alerts-subscribers">
            <h2 id="alerts-subscribers">Subscribers</h2>
            <div className="flags-count"><b>{count(r.totals.active)}</b><span>confirmed</span></div>
            <div className="access-rule"><span>Waiting to confirm</span><b>{count(r.totals.pending)}</b></div>
            <div className="access-rule"><span>Unsubscribed</span><b>{count(r.totals.unsubscribed)}</b></div>
            <div className="access-rule"><span>Bounced</span><b>{count(r.totals.bounced)}</b></div>
            <div className="access-rule"><span>Complaints</span><b>{count(r.totals.complained)}</b></div>
          </section>
          <section className="access-card" aria-labelledby="alerts-sent">
            <h2 id="alerts-sent">Sent, last 60 days</h2>
            <div className="flags-count"><b>{count(r.sends.reduce((n, s) => n + s.sent, 0))}</b><span>emails</span></div>
            <div className="access-rule"><span>{KIND.votes}</span><b>{count(r.sends.filter((s) => s.kind === "votes").reduce((n, s) => n + s.sent, 0))}</b></div>
            <div className="access-rule"><span>{KIND.agenda}</span><b>{count(r.sends.filter((s) => s.kind === "agenda").reduce((n, s) => n + s.sent, 0))}</b></div>
            <div className="access-rule"><span>Failed</span><b>{count(r.sends.reduce((n, s) => n + s.failed, 0))}</b></div>
          </section>
        </div>

        <section className="approvals-recent" aria-labelledby="alerts-wards">
          <h2 className="section-title" id="alerts-wards">By ward</h2>
          {wards.length === 0 ? <p className="empty-panel">No subscribers yet</p> : (
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="alerts-wards">
              <ListRow as="div" className="members__head ls-head" role="row"><Head width={70}>Ward</Head><Head width={90}>Confirmed</Head><Head width={110}>{KIND.votes}</Head><Head width={110}>{KIND.agenda}</Head><Head width={90}>Pending</Head><Head width={110}>Unsubscribed</Head><Head width={110}>Bounced</Head></ListRow>
              {wards.map((w) => (
                <ListRow key={w.ward} as="div" className="members__row" role="row">
                  <Cell width={70} className="members__active"><a href={`/ward/${w.ward}`}>{w.ward}</a></Cell>
                  <Cell width={90} className="members__active">{count(w.active)}</Cell>
                  <Cell width={110} className="members__active">{count(w.votes)}</Cell>
                  <Cell width={110} className="members__active">{count(w.agenda)}</Cell>
                  <Cell width={90} className="members__active">{count(w.pending)}</Cell>
                  <Cell width={110} className="members__active">{count(w.unsubscribed)}</Cell>
                  <Cell width={110} className="members__active">{count(w.bounced + w.complained)}</Cell>
                </ListRow>
              ))}
            </List>
          )}
        </section>

        <section className="approvals-recent" aria-labelledby="alerts-log">
          <h2 className="section-title" id="alerts-log">Send log</h2>
          {r.sends.length === 0 ? <p className="empty-panel">Nothing sent yet</p> : (
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="alerts-log">
              <ListRow as="div" className="members__head ls-head" role="row"><Head min={200}>Meeting</Head><Head width={130}>Alert</Head><Head width={60}>Ward</Head><Head width={60}>Sent</Head><Head width={70}>Failed</Head><Head width={80}>Bounced</Head><Head width={60}>Items</Head><Head width={130}>Last</Head></ListRow>
              {r.sends.map((s) => (
                <ListRow key={`${s.kind}:${s.meetingId}:${s.ward}`} as="div" className="members__row" role="row">
                  <Cell min={200} className="approvals__org"><RowTitle as="b"><a href={`/meeting/${encodeURIComponent(s.meetingId)}`}>{s.body ?? s.meetingId}</a></RowTitle><RowSub>{day(s.date)}</RowSub></Cell>
                  <Cell width={130} className="members__active">{KIND[s.kind]}</Cell>
                  <Cell width={60} className="members__active">{s.ward}</Cell>
                  <Cell width={60} className="members__active">{count(s.sent)}</Cell>
                  <Cell width={70} className="members__active">{count(s.failed + s.sending)}</Cell>
                  <Cell width={80} className="members__active">{count(s.bounced)}</Cell>
                  <Cell width={60} className="members__active">{s.items ?? ""}</Cell>
                  <Cell width={130} className="members__active">{when(s.lastAt)}</Cell>
                </ListRow>
              ))}
            </List>
          )}
        </section>

        <section className="approvals-recent" aria-labelledby="alerts-errors">
          <h2 className="section-title" id="alerts-errors">Latest errors</h2>
          {r.errors.length === 0 ? <p className="empty-panel">None</p> : (
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="alerts-errors">
              <ListRow as="div" className="members__head ls-head" role="row"><Head width={130}>Alert</Head><Head width={60}>Ward</Head><Head min={240}>Error</Head><Head width={130}>When</Head></ListRow>
              {r.errors.map((e, i) => (
                <ListRow key={i} as="div" className="members__row" role="row">
                  <Cell width={130} className="members__active">{KIND[e.kind as keyof typeof KIND] ?? e.kind}</Cell>
                  <Cell width={60} className="members__active">{e.ward}</Cell>
                  <Cell min={240} className="members__active admin-audit__reason">{e.error ?? ""}</Cell>
                  <Cell width={130} className="members__active">{when(e.at)}</Cell>
                </ListRow>
              ))}
            </List>
          )}
        </section>
      </>}
    </div>
  );
}
